/**
 * BarcodeScanner hook: detects barcode scanner input AND provides keyboard-only
 * navigation for speed-optimized disc review workflow.
 *
 * Barcode detection is global (document-level) so scans work regardless of
 * focus. Scanners are just keyboards — rapid digit sequences (< 100ms between
 * keystrokes) terminated by Tab or Enter are treated as barcodes.
 *
 * Keyboard shortcuts (when search input is focused):
 * - Enter: select first search result (if results visible, not a barcode)
 * - Escape: clear current movie and refocus search
 * - ArrowDown/ArrowUp: navigate search results
 */
export const BarcodeScanner = {
  mounted() {
    this.buffer = "";
    this.lastKeyTime = 0;
    this.highlightIndex = -1;
    this.INTER_KEY_THRESHOLD = 100;
    this.MIN_BARCODE_LENGTH = 8;
    this.MAX_BARCODE_LENGTH = 18;

    // Global listener — catches scanner input even when search isn't focused
    this.handleGlobalKeyDown = (e) => {
      const now = Date.now();
      const timeSinceLastKey = now - this.lastKeyTime;
      const isTerminator = e.key === "Tab" || e.key === "Enter";

      if (isTerminator && this.buffer.length >= this.MIN_BARCODE_LENGTH) {
        e.preventDefault();
        e.stopPropagation();
        const barcode = this.buffer;
        this.buffer = "";
        this.highlightIndex = -1;
        this.pushEvent("barcode_scan", { barcode });
        const input = this.el.querySelector("input");
        if (input) input.value = "";
        return;
      }

      if (/^\d$/.test(e.key)) {
        if (timeSinceLastKey > this.INTER_KEY_THRESHOLD) {
          this.buffer = "";
        }
        this.buffer += e.key;
        this.lastKeyTime = now;
        if (this.buffer.length > this.MAX_BARCODE_LENGTH) {
          this.buffer = this.buffer.slice(-this.MAX_BARCODE_LENGTH);
        }
        return;
      }

      if (!isTerminator) {
        this.buffer = "";
      }
    };

    // Scoped listener — keyboard navigation only when the hook element has focus
    this.handleLocalKeyDown = (e) => {
      const results = this.el.querySelectorAll("[data-result-id]");

      if (e.key === "Enter" && results.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        const idx = Math.max(0, this.highlightIndex);
        const target = results[idx];
        if (target) {
          target.click();
        }
        this.highlightIndex = -1;
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        this.highlightIndex = -1;
        this.pushEvent("clear_movie", {});
        requestAnimationFrame(() => {
          const input = this.el.querySelector("input");
          if (input) {
            input.value = "";
            input.focus();
          }
        });
        return;
      }

      if (e.key === "ArrowDown" && results.length > 0) {
        e.preventDefault();
        this.highlightIndex = Math.min(this.highlightIndex + 1, results.length - 1);
        this._updateHighlight(results);
        return;
      }

      if (e.key === "ArrowUp" && results.length > 0) {
        e.preventDefault();
        this.highlightIndex = Math.max(this.highlightIndex - 1, 0);
        this._updateHighlight(results);
        return;
      }
    };

    document.addEventListener("keydown", this.handleGlobalKeyDown, true);
    this.el.addEventListener("keydown", this.handleLocalKeyDown, true);

    this.handleEvent("focus_search", () => {
      requestAnimationFrame(() => {
        const input = this.el.querySelector("input");
        if (input) {
          input.value = "";
          input.focus();
        }
      });
    });
  },

  _updateHighlight(results) {
    results.forEach((el, i) => {
      if (i === this.highlightIndex) {
        el.classList.add("bg-blue-100");
        el.classList.remove("hover:bg-blue-50");
        el.scrollIntoView({ block: "nearest" });
      } else {
        el.classList.remove("bg-blue-100");
        el.classList.add("hover:bg-blue-50");
      }
    });
  },

  destroyed() {
    document.removeEventListener("keydown", this.handleGlobalKeyDown, true);
    this.el.removeEventListener("keydown", this.handleLocalKeyDown, true);
  },
};
